import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Skeleton,
  Alert,
  useTheme,
  useMediaQuery,
  Chip,
  CircularProgress,
  Link
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import TelegramIcon from '@mui/icons-material/Telegram';
import { motion } from 'framer-motion';

// Маппинг английских названий категорий на русские
const CATEGORY_LOCALIZATION = {
  'sauna': 'Баня',
  'health': 'Здоровье',
  'rent': 'Аренда',
  'outdoor': 'Отдых на природе',
  'maintenance': 'Обслуживание домика',
  'cooking': 'Готовка и кухня',
  'cleaning': 'Уборка',
  'heating': 'Отопление',
  'garden': 'Сад и огород',
  'entertainment': 'Развлечения',
  'security': 'Безопасность',
  'other': 'Другое'
};

// Эмодзи для категорий
const CATEGORY_EMOJIS = {
  'Баня': '🛁',
  'Здоровье': '💊',
  'Аренда': '🏠',
  'Отдых на природе': '🌲',
  'Обслуживание домика': '🔧',
  'Готовка и кухня': '👨‍🍳',
  'Уборка': '🧹',
  'Отопление': '🔥',
  'Сад и огород': '🌱',
  'Развлечения': '🎮',
  'Безопасность': '🛡️',
  'Другое': '📝'
};

const FeaturedTips = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTips = async () => {
      try {
        const response = await axios.get('/api/tips', {
          params: {
            page: 1,
            limit: 3,
            status: 'published',
            featured: true
          }
        });

        if (response.data && response.data.tips) {
          setTips(response.data.tips);
        } else {
          // Если нет избранных советов, загружаем обычные
          const regularResponse = await axios.get('/api/tips', {
            params: {
              page: 1,
              limit: 3,
              status: 'published'
            }
          });
          
          if (regularResponse.data && regularResponse.data.tips) {
            setTips(regularResponse.data.tips);
          }
        }
      } catch (error) {
        console.error('Error fetching tips:', error);
        setError('Не удалось загрузить полезные советы');
      } finally {
        setLoading(false);
      }
    };

    fetchTips();
  }, []);

  // Функция для форматирования даты
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(date);
  };
  
  // Функция для локализации категории
  const localizeCategory = (category) => {
    if (!category) return '';
    return CATEGORY_LOCALIZATION[category] || category;
  };
  
  // Функция для получения эмодзи категории
  const getCategoryEmoji = (category) => {
    const localizedCategory = localizeCategory(category);
    return CATEGORY_EMOJIS[localizedCategory] || '📝';
  };

  // Анимация для элементов
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  // Заглушка для загрузки
  if (loading) {
    return (
      <Box component="section" sx={{ py: 8, backgroundColor: theme.palette.background.paper }}>
        <Container>
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <Typography variant="h4" component="h2" gutterBottom>
              Полезные советы
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Загрузка советов...
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {[1, 2, 3].map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Skeleton variant="rectangular" height={200} />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Skeleton variant="text" height={32} width="80%" />
                    <Skeleton variant="text" height={20} width="60%" />
                    <Skeleton variant="text" height={60} />
                  </CardContent>
                  <CardActions>
                    <Skeleton variant="rectangular" height={36} width={120} />
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  }

  // Сообщение об ошибке
  if (error) {
    return (
      <Box component="section" sx={{ py: 8, backgroundColor: theme.palette.background.paper }}>
        <Container>
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  // Если нет советов, не показываем секцию
  if (tips.length === 0) {
    return null;
  }

  return (
    <Box component="section" sx={{ py: 8, backgroundColor: theme.palette.background.paper }}>
      <Container>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={containerVariants}
        >
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
              <LightbulbIcon color="primary" sx={{ fontSize: 32, mr: 1 }} />
              <Typography variant="h4" component="h2">
                Полезные советы
              </Typography>
            </Box>
            <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto', mb: 2 }}>
              Рекомендации и советы для комфортного отдыха
            </Typography>
            
            {/* Блок с информацией о телеграм-боте */}
            <Alert 
              severity="info" 
              icon={<TelegramIcon />}
              sx={{ 
                maxWidth: 700, 
                mx: 'auto', 
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                '& .MuiAlert-icon': {
                  color: '#0088cc', // Цвет Telegram
                  fontSize: '2rem',
                  alignSelf: 'center'
                }
              }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  Все полезные советы доступны в нашем Telegram-боте!
                </Typography>
                <Typography variant="body2">
                  Подписывайтесь на <Link href="https://t.me/DomikTyumenBot" target="_blank" rel="noopener" sx={{ fontWeight: 'bold' }}>@DomikTyumenBot</Link> и получайте советы прямо в мессенджере.
                </Typography>
              </Box>
            </Alert>
          </Box>

          <Grid container spacing={3}>
            {tips.map((tip) => (
              <Grid item xs={12} sm={6} md={4} key={tip.id}>
                <motion.div variants={itemVariants}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: theme.shadows[10],
                      }
                    }}
                  >
                    <CardMedia
                      component="img"
                      height={200}
                      image={tip.featuredImage || '/assets/placeholder-tip.jpg'}
                      alt={tip.title}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(tip.createdAt)}
                        </Typography>
                        {tip.category && (
                          <Chip 
                            label={`${getCategoryEmoji(tip.category)} ${localizeCategory(tip.category)}`} 
                            size="small" 
                            color="primary" 
                            variant="outlined" 
                          />
                        )}
                      </Box>
                      <Typography variant="h6" component="h3" gutterBottom>
                        {tip.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                      }}>
                        {tip.excerpt || tip.content.substring(0, 150) + '...'}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button 
                        component={RouterLink} 
                        to={`/tips/${tip.slug || tip.id}`} 
                        endIcon={<ArrowForwardIcon />}
                        sx={{ ml: 'auto' }}
                      >
                        Читать далее
                      </Button>
                    </CardActions>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: 5, textAlign: 'center' }}>
            <Button 
              component={RouterLink} 
              to="/tips" 
              variant="contained" 
              color="primary" 
              endIcon={<ArrowForwardIcon />}
              size="large"
            >
              Все советы
            </Button>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default FeaturedTips;
